<script lang="ts" setup>
const props = withDefaults(
  defineProps<{
    modelValue?: boolean;
  }>(),
  {
    modelValue: false,
  },
);

const emit = defineEmits<{
  (event: 'update:modelValue', value: boolean): void;
  (event: 'hide'): void;
}>();

const attrs = useAttrs();

const isVisible = ref<boolean>(props.modelValue);
let timer: ReturnType<typeof setTimeout>;

const leftPos = ref<number>(0);
const topPos = ref<number>(0);

const refEl = ref<HTMLDivElement>();
const refInner = ref<HTMLDivElement>();

const OFFSET = 16;

const showTooltip = function showTooltip(e: Event) {
  timer = setTimeout(() => {
    const { top, left, width } = (
      e.target as HTMLDivElement
    ).getBoundingClientRect();

    leftPos.value = left + width / 2;
    topPos.value = top - OFFSET;

    if (refEl.value) {
      document.body.appendChild(refEl.value);
    }

    isVisible.value = true;

    emit('update:modelValue', true);

    nextTick(() => {
      if (!refInner.value) return;

      const { left: elLeft, width: elWidth } =
        refInner.value.getBoundingClientRect();

      if (elLeft < 0) {
        refInner.value.style.left = '16px';
      }

      if (elLeft + elWidth > window.innerWidth) {
        const rightOffset = elLeft + elWidth - window.innerWidth + 16;

        refInner.value.style.right = `${rightOffset}px`;
      }
    });
  }, 300);
};

const hideTooltip = function hideTooltip() {
  clearTimeout(timer);

  isVisible.value = false;

  refEl.value?.remove();

  emit('update:modelValue', false);
  emit('hide');
};

onMounted(() => {
  refEl.value?.parentNode?.addEventListener('mouseenter', showTooltip);
  refEl.value?.parentNode?.addEventListener('mouseleave', hideTooltip);

  // window.addEventListener('scroll', hideTooltip);
});

onUnmounted(() => {
  refEl.value?.parentNode?.removeEventListener('mouseenter', showTooltip);
  refEl.value?.parentNode?.removeEventListener('mouseleave', hideTooltip);

  // window.removeEventListener('scroll', hideTooltip);
});
</script>

<template>
  <transition ref="refEl">
    <div
      v-if="isVisible"
      v-bind="attrs"
      class="custom-tooltip text-body-s"
      :style="{
        top: topPos + 'px',
        left: leftPos + 'px',
      }"
    >
      <div
        ref="refInner"
        class="custom-tooltip__inner"
      >
        <slot></slot>

        <svg
          class="custom-tooltip__arrow"
          width="17"
          height="8"
          viewBox="0 0 17 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            class="custom-tooltip__arrow-inner"
            d="M8.5 0L16.5 8H0.5L8.5 0Z"
            stroke-width=".5"
            stroke-linecap="round"
          />
        </svg>
      </div>
    </div>
  </transition>
</template>

<style lang="scss" scoped>
.custom-tooltip {
  $this: 'custom-tooltip';

  position: fixed;
  z-index: 9999;
  pointer-events: none;
  width: 0;
  height: 0;
  overflow: visible;
  animation: slidein 0.2s ease forwards;

  &__inner {
    position: relative;
    padding: 8px 12px;
    color: $color-white;
    border: 1px solid $color-new-black-600;
    background-color: $color-new-black-900;
    border-radius: $brd-8;
    width: max-content;
    max-width: 320px;
    transform: translate(-50%, -100%);
  }

  &__arrow {
    position: absolute;
    z-index: 1;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%) rotate(180deg);
  }

  &__arrow-inner {
    fill: $color-new-black-900;
    stroke: $color-new-black-900;
  }
}

@keyframes slidein {
  from {
    transform: translateY(10px);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
